import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";
import { Send, Loader2, Sparkles, RotateCcw, Globe, Share2, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const SUGGESTIONS = {
  "Star Trek": [
    "What is the Prime Directive and why is it important?",
    "Explain the history of the Federation-Klingon conflict",
    "Who is Q and what are his powers?",
  ],
  "Star Wars": [
    "How does the Force work and what are its two sides?",
    "What caused Anakin Skywalker's fall to the dark side?",
    "Explain the significance of the Mandalorian Darksaber",
  ],
  "Marvel Universe": [
    "What are the Infinity Stones and what does each one do?",
    "How did Tony Stark become Iron Man?",
    "Explain the Multiverse and how it works in Marvel",
  ],
  "DC Universe": [
    "What is Batman's greatest weakness?",
    "Explain the history of the Justice League",
    "What are the different Lantern Corps and their emotions?",
  ],
  "Lord of the Rings": [
    "What is the One Ring and how was it created?",
    "Who are the Istari and what is their purpose in Middle-earth?",
    "Explain the history of Gondor and its line of kings",
  ],
  "Game of Thrones": [
    "What is the prophecy of Azor Ahai?",
    "Explain the history of the Targaryen dynasty",
    "What are the White Walkers and where did they come from?",
  ],
  "The Witcher": [
    "What are Witchers and how are they made?",
    "Explain the Law of Surprise and its significance",
    "What is the Conjunction of the Spheres?",
  ],
};

const LoreExplorerPage = () => {
  const [searchParams] = useSearchParams();
  const [universes, setUniverses] = useState([]);
  const [selectedUniverse, setSelectedUniverse] = useState("");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => crypto.randomUUID());
  const chatEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const fetchUniverses = async () => {
      try {
        const res = await axios.get(`${API}/universes`);
        setUniverses(res.data);
        const preselect = searchParams.get("universe");
        if (preselect) {
          const match = res.data.find(u => u.slug === preselect || u.name === preselect);
          if (match) setSelectedUniverse(match.name);
        }
      } catch {
        toast.error("Failed to load universes");
      }
    };
    fetchUniverses();
  }, [searchParams]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendMessage = async (text) => {
    if (!text?.trim() || !selectedUniverse) return;
    const userMsg = { role: "user", content: text.trim() };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setLoading(true);

    try {
      const res = await axios.post(`${API}/ai/lore-chat`, {
        universe_name: selectedUniverse,
        question: text.trim(),
        session_id: sessionId,
        history: messages.slice(-6),
      });
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: res.data.answer },
      ]);
    } catch (err) {
      toast.error("The Lore Oracle is unavailable. Try again.");
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: "I'm having trouble connecting right now. Please try again in a moment." },
      ]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  const resetChat = () => {
    setMessages([]);
    setInput("");
    setShareUrl(null);
  };

  const [sharing, setSharing] = useState(false);
  const [shareUrl, setShareUrl] = useState(null);

  const shareConversation = async () => {
    if (messages.length < 2) {
      toast.error("Have a conversation first before sharing!");
      return;
    }
    setSharing(true);
    try {
      const res = await axios.post(`${API}/lore/share`, {
        universe_name: selectedUniverse,
        messages: messages,
      });
      const url = `${window.location.origin}/shared/${res.data.share_id}`;
      setShareUrl(url);
      await navigator.clipboard.writeText(url);
      toast.success("Link copied to clipboard!");
    } catch {
      toast.error("Failed to create share link");
    } finally {
      setSharing(false);
    }
  };

  const currentSuggestions = SUGGESTIONS[selectedUniverse] || [];

  return (
    <div className="min-h-screen bg-[var(--bg-base)] flex flex-col">
      <SEOHead
        title={selectedUniverse ? `${selectedUniverse} Lore Explorer` : "Lore Explorer"}
        description={`Ask the AI Lore Oracle anything about ${selectedUniverse || 'your favorite fictional universes'}.`}
      />
      <Navbar />
      <main className="flex-1 pt-20 pb-8 px-4 max-w-4xl mx-auto w-full flex flex-col">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-amber-500/20 bg-amber-500/5 mb-3">
            <Sparkles className="w-3.5 h-3.5 text-amber-500" />
            <span className="text-xs font-mono tracking-wider text-amber-500 uppercase">AI-Powered</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-white font-['Outfit'] mb-2">Lore Explorer</h1>
          <p className="text-slate-400 text-sm max-w-md mx-auto">
            Ask anything about your favorite fictional universes. The Lore Oracle knows all.
          </p>
        </div>

        {/* Universe Selector */}
        <div className="flex items-center gap-3 mb-4 justify-center">
          <Globe className="w-4 h-4 text-slate-500" />
          <Select value={selectedUniverse} onValueChange={(v) => { setSelectedUniverse(v); resetChat(); }}>
            <SelectTrigger className="w-64 bg-[var(--bg-surface)] border-white/10 text-white" data-testid="lore-universe-select">
              <SelectValue placeholder="Choose a universe" />
            </SelectTrigger>
            <SelectContent className="bg-[#111827] border-white/10">
              {universes.map((u) => (
                <SelectItem key={u.id} value={u.name}>{u.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {messages.length > 0 && (
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={shareConversation}
                disabled={sharing}
                className="text-amber-500 hover:text-amber-400"
                data-testid="lore-share-btn"
              >
                {shareUrl ? <Check className="w-4 h-4" /> : <Share2 className="w-4 h-4" />}
              </Button>
              <Button variant="ghost" size="sm" onClick={resetChat} className="text-slate-500 hover:text-white" data-testid="lore-reset-btn">
                <RotateCcw className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>

        {/* Chat Area */}
        <div className="flex-1 flex flex-col min-h-0 bg-[var(--bg-surface)] rounded-xl border border-white/5 overflow-hidden">
          <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4" data-testid="lore-chat-area">
            {/* Empty State */}
            {messages.length === 0 && selectedUniverse && (
              <div className="flex flex-col items-center justify-center h-full py-12">
                <div className="w-16 h-16 rounded-full bg-amber-500/10 flex items-center justify-center mb-4">
                  <Sparkles className="w-8 h-8 text-amber-500" />
                </div>
                <p className="text-white font-['Outfit'] font-semibold text-lg mb-1">
                  Lore Oracle: {selectedUniverse}
                </p>
                <p className="text-slate-500 text-sm mb-6">Ask me anything about this universe</p>
                {currentSuggestions.length > 0 && (
                  <div className="flex flex-col gap-2 w-full max-w-md">
                    {currentSuggestions.map((q, i) => (
                      <button
                        key={i}
                        onClick={() => sendMessage(q)}
                        className="text-left px-4 py-3 rounded-lg bg-white/[0.03] border border-white/5 text-sm text-slate-300 hover:bg-amber-500/10 hover:border-amber-500/20 hover:text-white transition-all"
                        data-testid={`lore-suggestion-${i}`}
                      >
                        {q}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {messages.length === 0 && !selectedUniverse && (
              <div className="flex flex-col items-center justify-center h-full py-12">
                <Globe className="w-12 h-12 text-slate-600 mb-4" />
                <p className="text-slate-500">Select a universe above to begin exploring its lore.</p>
              </div>
            )}

            {/* Messages */}
            {messages.map((msg, i) => (
              <div
                key={i}
                className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                data-testid={`lore-msg-${i}`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                    msg.role === "user"
                      ? "bg-amber-500/15 border border-amber-500/20 text-white"
                      : "bg-white/[0.04] border border-white/5 text-slate-300"
                  }`}
                >
                  {msg.role === "assistant" && (
                    <div className="flex items-center gap-1.5 mb-2">
                      <Sparkles className="w-3 h-3 text-amber-500" />
                      <span className="text-[10px] font-mono tracking-wider text-amber-500 uppercase">Lore Oracle</span>
                    </div>
                  )}
                  <p className="text-sm leading-relaxed whitespace-pre-line">{msg.content}</p>
                </div>
              </div>
            ))}

            {/* Loading indicator */}
            {loading && (
              <div className="flex justify-start" data-testid="lore-loading">
                <div className="bg-white/[0.04] border border-white/5 rounded-2xl px-4 py-3 flex items-center gap-2">
                  <Sparkles className="w-3 h-3 text-amber-500" />
                  <span className="text-[10px] font-mono tracking-wider text-amber-500 uppercase">Lore Oracle</span>
                  <Loader2 className="w-4 h-4 text-amber-500 animate-spin ml-2" />
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>

          {/* Input */}
          <form onSubmit={handleSubmit} className="p-3 sm:p-4 border-t border-white/5 bg-[var(--bg-base)]/50">
            <div className="flex gap-2">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={selectedUniverse ? `Ask about ${selectedUniverse}...` : "Select a universe first"}
                disabled={!selectedUniverse || loading}
                className="flex-1 bg-[var(--bg-elevated)] border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-amber-500/30 transition-colors disabled:opacity-40"
                data-testid="lore-input"
              />
              <Button
                type="submit"
                disabled={!input.trim() || !selectedUniverse || loading}
                className="bg-amber-500 hover:bg-amber-600 text-black rounded-xl px-4"
                data-testid="lore-send-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </div>
          </form>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default LoreExplorerPage;
